import { v4 as uuidv4 } from "uuid";

import { Transaction } from "src/interfaces";
import type {
  SplitRow,
  TransactionFormModel,
} from "src/interfaces/transaction.interface";

import firebase from "firebase/app";
import "firebase/functions";
import Bluebird from "bluebird";
import { chunk, reduce, values } from "lodash";
import { getUserIdSync, getUserId } from "src/config/storage";
import { MaximumBatchSize } from "src/config/firestore";
import firestore from "src/apiService/firestore";
import {
  DefaultAccounts,
  isBankTransfer,
  TransfersAccountName,
} from "src/utils/general-ledger-accounts";
import { computeMinSaleDate, computeMaxSaleDate } from "src/utils/sales";

import { consolidateOrders } from "./ebay";
import { deleteUploadedFile, uploadFile } from "src/utils/storage";
import { mapTransactionFromPayload } from "src/utils/transaction";
import FirebaseBatcher from "src/utils/FirebaseBatcher";

const db = firestore();

function linkedItemRef(type: "sale" | "inventory", itemId: string) {
  return db.collection(type === "sale" ? "Sales" : "Inventory").doc(itemId);
}

/**
 * Receipts (images and PDFs) are displayed straight from Storage, so they must
 * be stored with their real content type rather than the `application/json`
 * default used for marketplace sync dumps.
 */
function receiptContentType(file: File) {
  if (file.type) return file.type;
  if (file.name.toLowerCase().endsWith(".pdf")) return "application/pdf";
  return "application/octet-stream";
}

export async function deleteTransaction(
  id: string,
  consolidate = true
): Promise<string | undefined> {
  const [isReadOnly, orderId, journalEntryId, receipt_filename] =
    await db.runTransaction(async (t) => {
      let orderId = undefined;
      let journalEntryId = undefined;
      let receipt_filename = undefined;
      const doc = db.collection("Transactions").doc(id);
      const snapshot = await doc.get();
      const data = snapshot.data();
      const isReadOnly = !!data?.read_only;
      const linkedItemSnaps = new Map<
        string,
        firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
      >();

      if (!isReadOnly) {
        if (data?.orderId) orderId = data.orderId;
        if (data?.journal_entry_id) journalEntryId = data.journal_entry_id;
        if (data?.receipt_filename) receipt_filename = data.receipt_filename;

        if (data?.items) {
          const linkedItemRefs = new Map<
            string,
            firebase.firestore.DocumentReference<firebase.firestore.DocumentData>
          >();
          for (const { type, id: itemId } of data.items) {
            if (type !== "sale" && type !== "inventory") continue;
            const key = `${type}:${itemId}`;
            if (linkedItemRefs.has(key)) continue;
            linkedItemRefs.set(key, linkedItemRef(type, itemId));
          }
          for (const [key, ref] of linkedItemRefs.entries()) {
            try {
              linkedItemSnaps.set(key, await t.get(ref));
            } catch (e) {
              console.warn(
                "[transactions] deleteTransaction: linked item read failed (e.g. permission); treating as unlinked",
                { key, error: e }
              );
            }
          }
        }

        if (data?.mapped) {
          try {
            const mappedRef = db.collection("Transactions").doc(data.mapped);
            const mappedSnap = await t.get(mappedRef);
            if (mappedSnap.exists) {
              await t.update(mappedRef, {
                mapped: firebase.firestore.FieldValue.delete(),
              });
            }
          } catch (e) {
            console.warn(
              "[transactions] deleteTransaction: failed to unlink mapped transaction; continuing delete",
              { transactionId: id, mappedId: data.mapped, error: e }
            );
          }
        }

        if (data?.items) {
          for (const { type, id: itemId } of data.items) {
            if (type === "sale" || type === "inventory") {
              const key = `${type}:${itemId}`;
              const itemSnap = linkedItemSnaps.get(key);
              if (!itemSnap?.exists) continue;
              const itemRef = linkedItemRef(type, itemId);
              await t.update(itemRef, {
                transaction_inventory_id:
                  firebase.firestore.FieldValue.delete(),
                bank_account: firebase.firestore.FieldValue.delete(),
              });
            }
          }
        }

        await t.delete(doc);
      }

      return [isReadOnly, orderId, journalEntryId, receipt_filename];
    });

  if (isReadOnly) return;

  if (journalEntryId) {
    // This should be done inside the transaction, but client transactions can't do a search (ie.: where) query
    await deleteJournalEntry(journalEntryId);
  }

  if (receipt_filename) {
    await deleteUploadedFile(receipt_filename);
  }

  if (consolidate && orderId) await consolidateOrders({ orderIds: [orderId] });
  return orderId;
}

export async function deleteJournalEntry(id: string) {
  const user = await getUserId();
  const snapshot = await db
    .collection("Transactions")
    .where("user", "==", user)
    .where("journal_entry_id", "==", id)
    .get();

  for (const doc of snapshot.docs) {
    await doc.ref.delete();
  }
}

export async function getJournalEntry(id: string) {
  const user = await getUserId();
  const snapshot = await db
    .collection("Transactions")
    .where("user", "==", user)
    .where("journal_entry_id", "==", id)
    .get();

  if (snapshot.empty) return undefined;

  const rows = snapshot.docs.map((doc, index) => {
    const data = doc.data() as any;
    return {
      ...data,
      id: doc.id,
      key: data.key || doc.id || `${index}`,
      date: data?.date?.toDate ? data.date.toDate() : data?.date,
    };
  });

  return {
    id,
    date: rows[0]?.date || new Date(),
    rows,
  };
}

//Nico -- I'm thinking we should move this to the server side.
//The edits I made were to help speed up the execution, and they do seem to help,
//but with all the checks, it can take a while to complete. I'm concerned that
//we won't reliably complete the deletes that users initiate because they might
//refresh their page before the deletes are complete.

//In line with this thinking, on the update for the categorize expenses
//screen, I'd like to implement a "fire and forget" approach when users delete a
//transaction so that we can deliver a smooth experience when they delete
//transactions, while also ensuring the process will reliably complete or
//throw an error if it doesn't.

//Let me know what you think!

export async function deleteTransactions(ids: string[]) {
  if (!ids.length) return;

  // Step 1: Batch fetch all documents to categorize them
  const simpleIds: string[] = [];
  const complexIds: string[] = [];
  const orderIds: string[] = [];
  const splitGroupIds = new Set<string>();
  const visited = new Set<string>();

  for (const batchIds of chunk(ids, MaximumBatchSize)) {
    const snapshots = await Promise.all(
      batchIds.map((id) => db.collection("Transactions").doc(id).get())
    );

    for (const snapshot of snapshots) {
      const data = snapshot.data();
      if (!data || data.read_only) continue;
      visited.add(snapshot.id);

      // If this row belongs to a split group, expand to include siblings so we
      // never leave orphaned split children behind.
      if (data.split_group_id) splitGroupIds.add(data.split_group_id);

      // Track orderIds for consolidation
      if (data.orderId) orderIds.push(data.orderId);

      // Check if transaction needs complex cleanup
      const needsComplexCleanup =
        data.mapped ||
        data.items ||
        data.journal_entry_id ||
        data.receipt_filename;

      if (needsComplexCleanup) {
        complexIds.push(snapshot.id);
      } else {
        simpleIds.push(snapshot.id);
      }
    }
  }

  // Step 1b: Expand split groups to include any siblings we didn't receive.
  if (splitGroupIds.size) {
    const groupIdsArray = Array.from(splitGroupIds);
    const user = await getUserId();
    // Firestore `in` queries support up to 10 values.
    const siblingSnapshots = await Promise.all(
      chunk(groupIdsArray, 10).map((groupChunk) =>
        db
          .collection("Transactions")
          .where("user", "==", user)
          .where("split_group_id", "in", groupChunk)
          .get()
      )
    );
    for (const snap of siblingSnapshots) {
      for (const doc of snap.docs) {
        if (visited.has(doc.id)) continue;
        const data = doc.data();
        if (!data || data.read_only) continue;
        visited.add(doc.id);

        if (data.orderId) orderIds.push(data.orderId);

        const needsComplexCleanup =
          data.mapped ||
          data.items ||
          data.journal_entry_id ||
          data.receipt_filename;
        if (needsComplexCleanup) complexIds.push(doc.id);
        else simpleIds.push(doc.id);
      }
    }
  }

  // Step 2: Delete simple transactions with batch writes (fast!)
  const batch = new FirebaseBatcher();
  for (const id of simpleIds) {
    await batch.delete(db.collection("Transactions").doc(id));
  }
  await batch.commit();

  // Step 3: Delete complex transactions with full cleanup (parallel)
  if (complexIds.length > 0) {
    for (const batchIds of chunk(complexIds, MaximumBatchSize)) {
      await Promise.all(batchIds.map((id) => deleteTransaction(id, false)));
    }
  }

  // Step 4: Consolidate orders
  const cleared = [...new Set(orderIds)].filter(Boolean);
  if (cleared.length) {
    await consolidateOrders({ orderIds: cleared });
  }
}

/** Match to an existing transaction (only date/description allowed). */
export interface MappedOptionMatch {
  id: string;
  date: Date;
  description?: string;
}

/** Create the other leg of a transfer (requires bank account fields). */
export interface MappedOptionCreate {
  date: Date;
  bank_account_id: string;
  integration_name: string;
  description?: string;
}

/** Update existing mapped transaction with only bank account fields. */
export interface MappedOptionUpdate {
  bank_account_id: string;
  integration_name: string;
}

export type MappedOption =
  | "remove"
  | MappedOptionMatch
  | MappedOptionCreate
  | MappedOptionUpdate;

export function isMappedOptionMatch(
  opt: MappedOption
): opt is MappedOptionMatch {
  return typeof opt === "object" && opt !== null && "id" in opt && !!opt.id;
}

export function isMappedOptionCreate(
  opt: MappedOption
): opt is MappedOptionCreate {
  return (
    typeof opt === "object" &&
    opt !== null &&
    "bank_account_id" in opt &&
    "integration_name" in opt &&
    "date" in opt
  );
}

export function isMappedOptionUpdate(
  opt: MappedOption
): opt is MappedOptionUpdate {
  return (
    typeof opt === "object" &&
    opt !== null &&
    "bank_account_id" in opt &&
    "integration_name" in opt &&
    !("id" in opt) &&
    !("date" in opt)
  );
}

export async function addTransaction(
  transaction: Partial<Transaction>,
  options: { saleId?: string; mapped?: MappedOption } = {},
  newImageFile?: File
): Promise<{ id: string; receipt_filename?: string }> {
  const doc = transaction.id
    ? db.collection("Transactions").doc(transaction.id)
    : db.collection("Transactions").doc();
  const user = await getUserId();
  const data: any = {
    ...transaction,
    id: doc.id,
    user,
    ...(newImageFile
      ? {
          receipt_filename: `${user}/transactionReceipts/${doc.id}_${newImageFile.name}`,
        }
      : {}),
  };

  delete (data as any).vector;
  delete (data as any).accountDoc;
  delete (data as any).vendorDoc;
  delete (data as any).vendorName;

  let newOrderId: string | undefined = data.orderId;
  let oldOrderId: string | undefined = undefined;
  let oldAmount = data.amount;
  let oldAccount = data.gl_account_id;

  if (
    "orderId" in data &&
    (data.orderId === undefined || data.orderId === null)
  )
    data.orderId = firebase.firestore.FieldValue.delete();

  // A cleared vendor is `undefined` (`vendor?: string`) with the key still
  // present, matching VendorField and updateTransactionVendor. Firestore
  // rejects undefined, so an edit deletes the field and a create omits it.
  if ("vendor" in transaction && transaction.vendor == null) {
    if (transaction.id) {
      data.vendor = firebase.firestore.FieldValue.delete();
    } else {
      delete data.vendor;
    }
  }

  let glAccount: { type?: [string, string] } | null = null;
  if (data.gl_account_id) {
    const defaultAccount = DefaultAccounts.find(
      (a) => a.id === data.gl_account_id
    );
    if (defaultAccount) {
      glAccount = { type: defaultAccount.type };
    } else {
      const snap = await db
        .collection("GL_Accounts")
        .doc(data.gl_account_id)
        .get();
      glAccount = (snap.data() as any) || null;
    }
  } else if (data.account) {
    const defaultAccount = DefaultAccounts.find((a) => a.name === data.account);
    if (defaultAccount) {
      glAccount = { type: defaultAccount.type };
    } else {
      const snap = await db
        .collection("GL_Accounts")
        .where("user", "==", user)
        .where("name", "==", data.account)
        .limit(1)
        .get();
      if (!snap.empty) glAccount = (snap.docs[0].data() as any) || null;
    }
  }
  if (
    glAccount?.type &&
    isBankTransfer(glAccount.type) &&
    data.gl_account_id !== "transfers"
  ) {
    throw new Error(
      "Use the 'Balance Transfer' general ledger account for transfer transactions"
    );
  }

  if (!transaction.id && !data.items && !options.saleId && !options.mapped) {
    // The only case that we don't need to do a transaction is when we are creating a
    // new transaction that isn't mapped to another sale nor expense
    await doc.set(data);
  } else {
    [oldOrderId, oldAmount, oldAccount, newOrderId] = await db.runTransaction(
      async (t) => {
        let orderId: string | undefined = newOrderId;
        let oldOrderId: string | undefined = undefined;
        let oldAmount: number | undefined = undefined;
        let oldAccount: string | undefined = undefined;

        const isUpdate = !!transaction.id;
        const newGlAccountIsTransfer = data.gl_account_id === "transfers";
        let expenseData: Record<string, any> = {};
        const staleCurrentItems: Array<{
          type: "sale" | "inventory";
          id: string;
        }> = [];
        const linkedItemSnaps = new Map<
          string,
          firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
        >();
        const linkedItemRefs = new Map<
          string,
          firebase.firestore.DocumentReference<firebase.firestore.DocumentData>
        >();
        const mappedOpt = options.mapped;
        const mappedOptIsMatch =
          newGlAccountIsTransfer &&
          !!mappedOpt &&
          mappedOpt !== "remove" &&
          isMappedOptionMatch(mappedOpt);
        const mappedRef = mappedOptIsMatch
          ? db.collection("Transactions").doc(mappedOpt.id)
          : undefined;
        let mappedData: Record<string, unknown> | undefined;
        let existingMappedSnap:
          | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
          | undefined;
        let saleLinkSnap:
          | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
          | undefined;
        if (isUpdate) {
          const expense = await t.get(doc);
          if (!expense.exists) {
            throw new Error(
              `addTransaction: Transaction (#${doc.id}) doesn't exist`
            );
          }
          expenseData = expense.data() || {};
          oldOrderId = expenseData.orderId;
          oldAmount = expenseData.amount;
          oldAccount = expenseData.gl_account_id;

          if (expenseData.items) {
            for (const { type, id: itemId } of expenseData.items) {
              if (
                !data.items ||
                !data.items.find((i) => i.type === type && i.id === itemId)
              ) {
                if (type === "sale" || type === "inventory") {
                  const key = `${type}:${itemId}`;
                  if (!linkedItemRefs.has(key)) {
                    linkedItemRefs.set(key, linkedItemRef(type, itemId));
                  }
                }
              }
            }
          }
        }

        if (Array.isArray(data.items)) {
          for (const { type, id: itemId } of data.items) {
            if (type !== "sale" && type !== "inventory") continue;
            const key = `${type}:${itemId}`;
            if (!linkedItemRefs.has(key)) {
              linkedItemRefs.set(key, linkedItemRef(type, itemId));
            }
          }
        }

        for (const [key, ref] of linkedItemRefs.entries()) {
          try {
            linkedItemSnaps.set(key, await t.get(ref));
          } catch (e) {
            // Client rules can reject get() on docs the user cannot read (e.g. stale
            // sale/inventory id after delete). Omit snapshot so updates are skipped.
            console.warn(
              "[transactions] addTransaction: linked item read failed (e.g. permission); treating as unlinked",
              { key, error: e }
            );
          }
        }

        if (mappedRef) {
          const mappedSnap = await t.get(mappedRef);
          if (mappedSnap.exists) {
            mappedData = mappedSnap.data() || {};
          }
        }
        if (isUpdate && expenseData.mapped) {
          try {
            existingMappedSnap = await t.get(
              db.collection("Transactions").doc(expenseData.mapped)
            );
          } catch (e) {
            console.warn(
              "[transactions] addTransaction: failed to read previous mapped transaction",
              { transactionId: doc.id, mappedId: expenseData.mapped, error: e }
            );
          }
        }
        if (options.saleId) {
          saleLinkSnap = await t.get(
            db.collection("Sales").doc(options.saleId)
          );
          if (!saleLinkSnap.exists) {
            throw new Error(
              `addTransaction: Linked sale (#${options.saleId}) doesn't exist`
            );
          }
        }

        if (isUpdate) {
          if (
            (!newGlAccountIsTransfer || options.mapped === "remove") &&
            expenseData.mapped
          ) {
            try {
              if (existingMappedSnap?.exists) {
                await t.update(
                  db.collection("Transactions").doc(expenseData.mapped),
                  {
                    mapped: firebase.firestore.FieldValue.delete(),
                  }
                );
              }
            } catch (e) {
              console.warn(
                "[transactions] addTransaction: failed to unlink previous mapped transaction",
                {
                  transactionId: doc.id,
                  mappedId: expenseData.mapped,
                  error: e,
                }
              );
            }
            data.mapped = firebase.firestore.FieldValue.delete();
          } else if (
            newGlAccountIsTransfer &&
            expenseData.mapped &&
            options.mapped != null &&
            options.mapped !== "remove" &&
            isMappedOptionMatch(options.mapped) &&
            options.mapped.id !== expenseData.mapped
          ) {
            try {
              if (existingMappedSnap?.exists) {
                await t.update(
                  db.collection("Transactions").doc(expenseData.mapped),
                  {
                    mapped: firebase.firestore.FieldValue.delete(),
                  }
                );
              }
            } catch (e) {
              console.warn(
                "[transactions] addTransaction: failed to unlink replaced mapped transaction",
                {
                  transactionId: doc.id,
                  mappedId: expenseData.mapped,
                  error: e,
                }
              );
            }
          }

          if (expenseData.items) {
            for (const { type, id: itemId } of expenseData.items) {
              if (
                !data.items ||
                !data.items.find((i) => i.type === type && i.id === itemId)
              ) {
                if (type === "sale" || type === "inventory") {
                  const key = `${type}:${itemId}`;
                  const itemSnap = linkedItemSnaps.get(key);
                  if (!itemSnap?.exists) continue;
                  const itemRef = linkedItemRef(type, itemId);
                  await t.update(itemRef, {
                    transaction_inventory_id:
                      firebase.firestore.FieldValue.delete(),
                    bank_account: firebase.firestore.FieldValue.delete(),
                  });
                }
              }
            }
          }
        }

        if (newGlAccountIsTransfer && mappedOpt && mappedOpt !== "remove") {
          const amountForMapped = -(data.amount ?? 0);
          if (isMappedOptionMatch(mappedOpt)) {
            // XXX: check if the other transaction is from an inventory type?
            if (mappedData?.mapped && mappedData.mapped !== doc.id) {
              throw new Error(
                "The matching transaction is already mapped to another transaction."
              );
            }
            if (!mappedRef) {
              throw new Error("Mapped transaction reference is missing.");
            }
            const upd: Record<string, unknown> = {
              orderId: firebase.firestore.FieldValue.delete(),
              account: TransfersAccountName,
              gl_account_id: "transfers",
              mapped: doc.id,
              amount: amountForMapped,
              ...(mappedOpt.date != null && { date: mappedOpt.date }),
              ...(mappedOpt.description !== undefined && {
                description: mappedOpt.description,
              }),
            };
            if (!mappedData) {
              throw new Error("The matching transaction doesn't exist.");
            }
            await t.update(mappedRef, upd);
            data.mapped = mappedOpt.id;
          } else if (
            isMappedOptionUpdate(mappedOpt) &&
            isUpdate &&
            expenseData.mapped
          ) {
            const mappedRef = db
              .collection("Transactions")
              .doc(expenseData.mapped as string);
            if (existingMappedSnap?.exists) {
              await t.update(mappedRef, {
                integration_name: mappedOpt.integration_name,
                bank_account_id: mappedOpt.bank_account_id,
              });
            }
          } else if (isMappedOptionCreate(mappedOpt)) {
            const date = mappedOpt.date;
            const bank_account_id = mappedOpt.bank_account_id;
            const integration_name = mappedOpt.integration_name;
            if (
              date == null ||
              bank_account_id == null ||
              integration_name == null
            ) {
              throw new Error(
                "Creating a mapped transfer requires date, bank_account_id, and integration_name."
              );
            }
            const mappedDoc = db.collection("Transactions").doc();
            data.mapped = mappedDoc.id;
            await t.set(mappedDoc, {
              ...data,
              amount: amountForMapped,
              id: mappedDoc.id,
              account: TransfersAccountName,
              gl_account_id: "transfers",
              integration_name,
              bank_account_id,
              mapped: doc.id,
              date,
              description: mappedOpt.description || "",
            });
          }
          data.account = TransfersAccountName;
          data.gl_account_id = "transfers";
        }

        if (options.saleId)
          await t.update(db.collection("Sales").doc(options.saleId), {
            transaction_id: data.orderId || options.saleId,
          });

        if (Array.isArray(data.items)) {
          for (const { type, id: itemId } of data.items) {
            if (type === "sale" || type === "inventory") {
              const key = `${type}:${itemId}`;
              const itemSnap = linkedItemSnaps.get(key);
              if (!itemSnap?.exists) {
                staleCurrentItems.push({ type, id: itemId });
                continue;
              }
              const itemRef = linkedItemRef(type, itemId);
              const update: any = {
                transaction_inventory_id: data.id,
              };
              if (data.date) update.purchase_date = data.date;
              if (data.vendor) update.vendor = data.vendor;
              if (data.integration_name)
                update.bank_account = data.integration_name;

              if (type === "sale") {
                const mergedForDates = {
                  ...itemSnap.data(),
                  ...update,
                };
                update.min_date = computeMinSaleDate(mergedForDates);
                update.max_date = computeMaxSaleDate(mergedForDates);
              }

              await t.update(itemRef, update);
            }
          }

          if (staleCurrentItems.length) {
            const staleKey = new Set(
              staleCurrentItems.map((item) => `${item.type}:${item.id}`)
            );
            const cleanedItems = data.items.filter(
              ({ type, id }: { type: string; id: string }) =>
                !staleKey.has(`${type}:${id}`)
            );
            if (cleanedItems.length) {
              data.items = cleanedItems;
            } else {
              data.items = firebase.firestore.FieldValue.delete();
            }
          }
        } else {
          data.items = firebase.firestore.FieldValue.delete();
        }

        if (data.gl_account_id === "transfers") {
          orderId = undefined;
          data.orderId = firebase.firestore.FieldValue.delete();
        }

        await t.set(doc, data, { merge: true });
        return [oldOrderId, oldAmount, oldAccount, orderId];
      }
    );
  }

  const shouldConsolidate = transaction.id
    ? // If the expense already exists, we need to consolidate if the order id has changed
      !!newOrderId !== !!oldOrderId ||
      (!!newOrderId && !!oldOrderId && newOrderId !== oldOrderId) ||
      // The expense has an order id and the amount or the account has changed
      (newOrderId &&
        (oldAmount !== data.amount || oldAccount !== data.gl_account_id))
    : // If its a new expense, we only consolidate if it has an order id
      !!newOrderId;

  if (shouldConsolidate) {
    const orderIds = [
      ...new Set([oldOrderId, newOrderId].filter(Boolean)),
    ] as string[];

    if (orderIds.length) await consolidateOrders({ orderIds });
  }

  if (newImageFile) {
    await uploadFile(data.receipt_filename, newImageFile, {
      contentType: receiptContentType(newImageFile),
    });
    return { id: doc.id, receipt_filename: data.receipt_filename };
  }

  return { id: doc.id };
}

export const setTransactionsReviewed = async (transactionIds: string[]) => {
  if (transactionIds.length === 0) return;

  const batcher = new FirebaseBatcher();

  for (const transactionId of transactionIds) {
    const ref = db.collection("Transactions").doc(transactionId);
    await batcher.update(ref, { reviewed: true });
  }

  await batcher.commit();
};

export const removeTransactionReceipt = async (transactionId: any) => {
  const ref = db.collection("Transactions").doc(transactionId);
  return await ref.update({
    receipt_filename: firebase.firestore.FieldValue.delete(),
  });
};

export const addTransactionReceipt = async (
  transactionId: any,
  receiptFile: File
) => {
  const ref = db.collection("Transactions").doc(transactionId);
  const user = await getUserId();
  const filename = `${user}/transactionReceipts/${transactionId}_${receiptFile.name}`;
  await uploadFile(filename, receiptFile, {
    contentType: receiptContentType(receiptFile),
  });
  try {
    await ref.update({ receipt_filename: filename });
  } catch (e) {
    await discardUnreferencedReceipt(transactionId, filename);
    throw e;
  }
  return filename;
};

export const updateTransactionDescription = async (
  transactionId: any,
  description: string
) => {
  const ref = db.collection("Transactions").doc(transactionId);
  return await ref.set({ description: description }, { merge: true });
};

export async function updateTransactionAccountId(
  transactionId: any,
  accountId: string,
  account: string
) {
  const ref = db.collection("Transactions").doc(transactionId);
  return await ref.set({ gl_account_id: accountId, account }, { merge: true });
}

export async function batchUpdateTransactionAccountIds(
  transactionIds: string[],
  accountId: string,
  account: string
) {
  if (transactionIds.length === 0) return;

  const batcher = new FirebaseBatcher();

  for (const transactionId of transactionIds) {
    const ref = db.collection("Transactions").doc(transactionId);
    await batcher.update(ref, { gl_account_id: accountId, account });
  }

  await batcher.commit();
}

export const updateTransactionVendor = async (
  transactionId: string,
  vendor?: string
) => {
  const ref = db.collection("Transactions").doc(transactionId);
  return await ref.update({
    vendor:
      vendor !== undefined ? vendor : firebase.firestore.FieldValue.delete(),
  });
};

export async function batchUpdateTransactionVendors(
  transactionIds: string[],
  vendor?: string
) {
  if (transactionIds.length === 0) return;

  const batcher = new FirebaseBatcher();
  const value =
    vendor !== undefined ? vendor : firebase.firestore.FieldValue.delete();

  for (const transactionId of transactionIds) {
    const ref = db.collection("Transactions").doc(transactionId);
    await batcher.update(ref, { vendor: value });
  }

  await batcher.commit();
}

export interface BatchTransactionUpdate {
  gl_account_id?: string;
  account?: string;
  /** Present and `undefined` clears the field. */
  vendor?: string;
  bank_account_id?: string;
  integration_name?: string;
}

/**
 * Apply a partial update to many Transactions in a single batched write per
 * document. Set `vendor` to `undefined` (the key must be present) to clear it.
 */
export async function batchUpdateTransactions(
  transactionIds: string[],
  update: BatchTransactionUpdate
) {
  if (transactionIds.length === 0) return;
  const keys = Object.keys(update) as (keyof BatchTransactionUpdate)[];
  if (keys.length === 0) return;

  const payload: firebase.firestore.UpdateData = {};
  for (const key of keys) {
    const value = update[key];
    if (key === "vendor" && value == null) {
      payload.vendor = firebase.firestore.FieldValue.delete();
    } else if (value !== undefined) {
      payload[key] = value;
    }
  }

  if (Object.keys(payload).length === 0) return;

  const batcher = new FirebaseBatcher();
  for (const transactionId of transactionIds) {
    const ref = db.collection("Transactions").doc(transactionId);
    await batcher.update(ref, payload);
  }
  await batcher.commit();
}

export const updateTransactionHasRule = async (
  transactionId: any,
  hasRule: boolean
) => {
  const ref = db.collection("Transactions").doc(transactionId);
  return await ref.set({ has_rule: hasRule }, { merge: true });
};

// given a list of expenses  [{ user: expense.user, shipping_amount: 0, other_amount: 0, transaction_id: orderId }, ...]
// update corresponding sale's shipping_cost_analytics and other_fees
export const processExpenseToSalesUpdate = async (ebayExpensesArray: any) => {
  const user = getUserIdSync();
  const expensesByOrderId = reduce(
    ebayExpensesArray,
    (acc, expense) => {
      const orderId = expense.orderId;
      if (orderId && user === expense.user) {
        const isShipping = expense?.account === "Shipping Fees";
        const isOther =
          expense?.account === "Listing/Platform Fees" &&
          ["Adjustment", "Other Fees"].includes(expense?.transactionType);
        if (!acc[orderId]) {
          acc[orderId] = {
            user: expense.user,
            shipping_amount: 0,
            other_amount: 0,
            transaction_id: orderId,
          };
        }
        if (isShipping) {
          acc[orderId].shipping_amount += isNaN(+expense.amount)
            ? 0
            : +expense.amount;
        } else if (isOther) {
          acc[orderId].other_amount += isNaN(+expense.amount)
            ? 0
            : +expense.amount;
        }
      }
      return acc;
    },
    {}
  );
  return Bluebird.map(
    values(expensesByOrderId),
    async (expense) => {
      if (expense.shipping_amount || expense.other_amount) {
        const salesSnapshot = await db
          .collection("Sales")
          .where("user", "==", expense.user)
          .where("transaction_id", "==", expense.transaction_id)
          .limit(1)
          .get();
        const sales = salesSnapshot.docs.map((doc) => doc.data());
        if (sales.length === 1 && sales[0]?.id) {
          const sale = sales[0];
          const saleRef = db.collection("Sales").doc(sale.id);
          const previousSaleShippingCostAnalytics = isNaN(
            +sale.shipping_cost_analytics
          )
            ? 0
            : +sale.shipping_cost_analytics;
          const expenseShippingAmount = isNaN(+expense.shipping_amount)
            ? 0
            : +expense.shipping_amount;
          const previousSaleOtherFee = isNaN(+sale.other_fees)
            ? 0
            : +sale.other_fees;
          const expenseOtherAmount = isNaN(+expense.other_amount)
            ? 0
            : +expense.other_amount;
          await saleRef.set(
            {
              shipping_cost_analytics:
                previousSaleShippingCostAnalytics + expenseShippingAmount,
              other_fees: previousSaleOtherFee + expenseOtherAmount,
            },
            {
              merge: true,
            }
          );
        }
      }
    },
    { concurrency: 10 }
  );
};

export async function uploadExpensesFile(rows, metadata = {}) {
  const f = firebase.functions().httpsCallable("upload-uploadExpenses");

  return (await f({ rows, metadata })).data;
}

export async function bulkEditExpenses(rows, metadata = {}) {
  const f = firebase.functions().httpsCallable("upload-bulkEditExpenses");
  return (await f({ rows, metadata })).data;
}

export async function upsertJournalEntry({
  id,
  date,
  rows,
}: {
  id?: string;
  date: Date;
  rows: {
    id?: string;
    gl_account_id: string;
    account: string;
    amount: number;
    description: string;
  }[];
}) {
  const user = await getUserId();
  const journalEntryId = id || uuidv4();

  const transactionIds = await db.runTransaction(async (t) => {
    const ids: string[] = [];
    for (const row of rows) {
      const doc = row.id
        ? db.collection("Transactions").doc(row.id)
        : db.collection("Transactions").doc();
      const data = {
        ...row,
        date,
        id: doc.id,
        user,
        journal_entry_id: journalEntryId,
        integration_name: "Journal Entry",
      };
      ids.push(doc.id);
      await t.set(doc, data, { merge: true });
    }
    return ids;
  });

  if (id) {
    // Update process, we should delete "extra" transactions
    // This should be done in the db transaction, but we can't do a search (ie: where) query in a client transaction
    const snapshot = await db
      .collection("Transactions")
      .where("user", "==", user)
      .where("journal_entry_id", "==", id)
      .get();

    for (const doc of snapshot.docs) {
      if (transactionIds.includes(doc.id)) continue;
      await doc.ref.delete();
    }
  }
}

export interface SplitCommonFields {
  date?: Date;
  description?: string;
  /** Present and `undefined` removes the vendor from every row in the group. */
  vendor?: string;
  integration_name?: string;
  bank_account_id?: string;
  // Carries the originating upload id so split children are picked up by the
  // "delete by upload" flow alongside their parent.
  upload?: string;
}

export interface SplitRowInput {
  id?: string;
  amount: number;
  gl_account_id: string;
  account: string;
  description?: string;
}

/**
 * Create or update a split transaction: one `Transactions` doc per row, all
 * sharing the same `split_group_id`. The Cloud Function writes the kept rows
 * and deletes removed rows in one Firestore transaction. A replacement receipt
 * is uploaded here after that call returns the new Storage path.
 */
export async function upsertSplitTransaction({
  split_group_id,
  common,
  rows,
  newImageFile,
  existingReceiptFilename,
}: {
  split_group_id?: string;
  common: SplitCommonFields;
  rows: SplitRowInput[];
  newImageFile?: File;
  existingReceiptFilename?: string | null;
}): Promise<{
  split_group_id: string;
  ids: string[];
  receipt_filename?: string;
}> {
  if (!rows.length) {
    throw new Error("A split transaction must have at least one row.");
  }

  const fn = firebase.functions().httpsCallable("upsertSplitTransaction");
  // Callable payloads are JSON, which drops `undefined`. The function treats
  // `null` as "delete this field", so an explicit clear is sent as `null`.
  const commonPayload: Omit<SplitCommonFields, "vendor"> & {
    vendor?: string | null;
  } =
    "vendor" in common && common.vendor === undefined
      ? { ...common, vendor: null }
      : common;
  const response = await fn({
    ...(split_group_id ? { split_group_id } : {}),
    common: commonPayload,
    rows,
    ...(existingReceiptFilename !== undefined && !newImageFile
      ? { existing_receipt_filename: existingReceiptFilename }
      : {}),
    ...(newImageFile ? { new_receipt_name: newImageFile.name } : {}),
  });
  const data = response.data as {
    split_group_id?: string;
    ids?: string[];
    receipt_filename?: string;
  };
  if (!data?.split_group_id || !data.ids?.length) {
    throw new Error("Split save did not return the written rows");
  }
  if (newImageFile) {
    if (!data.receipt_filename) {
      throw new Error("Split save did not return a receipt path");
    }
    await uploadFile(data.receipt_filename, newImageFile, {
      contentType: receiptContentType(newImageFile),
    });
    return {
      split_group_id: data.split_group_id,
      ids: data.ids,
      receipt_filename: data.receipt_filename,
    };
  }

  return { split_group_id: data.split_group_id, ids: data.ids };
}

/**
 * Point every row of a split group at a new receipt, or clear it when no file
 * is given. The file is uploaded before the call; the Cloud Function deletes
 * the receipt it replaced.
 */
export async function setSplitGroupReceipt(
  splitGroupId: string,
  primaryId: string,
  receiptFile?: File
): Promise<{ ids: string[]; receipt_filename: string | null }> {
  let receiptFilename: string | null = null;
  if (receiptFile) {
    const user = await getUserId();
    receiptFilename = `${user}/transactionReceipts/${primaryId}_${receiptFile.name}`;
    await uploadFile(receiptFilename, receiptFile, {
      contentType: receiptContentType(receiptFile),
    });
  }
  const fn = firebase.functions().httpsCallable("setSplitGroupReceipt");
  try {
    const response = await fn({
      split_group_id: splitGroupId,
      receipt_filename: receiptFilename,
    });
    const data = response.data as { ids?: string[] };
    return { ids: data?.ids ?? [], receipt_filename: receiptFilename };
  } catch (e) {
    if (receiptFilename) {
      await discardUnreferencedReceipt(primaryId, receiptFilename);
    }
    throw e;
  }
}

/**
 * Best-effort removal of a receipt uploaded for a save that then failed. Keeps
 * the file when the transaction already points at it: the upload may have
 * overwritten the current receipt (same file name), or the write may have
 * committed before the client saw the error.
 */
async function discardUnreferencedReceipt(
  transactionId: string,
  receiptFilename: string
) {
  try {
    const snapshot = await db
      .collection("Transactions")
      .doc(transactionId)
      .get();
    if (snapshot.data()?.receipt_filename === receiptFilename) return;
    await deleteUploadedFile(receiptFilename);
  } catch (e) {
    console.warn(`Could not clean up receipt ${receiptFilename}`, e);
  }
}

/**
 * Fetch a single transaction the current user owns, with the Firestore
 * timestamps turned into `Date`s. Returns `undefined` when the doc is missing
 * or belongs to someone else.
 */
export async function getTransactionById(
  id: string
): Promise<Transaction | undefined> {
  const [user, snapshot] = await Promise.all([
    getUserId(),
    db.collection("Transactions").doc(id).get(),
  ]);
  if (!snapshot.exists) return undefined;
  const data = snapshot.data();
  if (!data || data.user !== user) return undefined;

  return mapTransactionFromPayload(data);
}

/**
 * Load a split transaction for the edit form. Returns `undefined` when the
 * doc is missing or belongs to another user. Throws when the transaction
 * exists but is not part of a split group.
 *
 * Sibling rows come back as `split.rows` with the requested row first, plus
 * an explicit `split.total`.
 */
export async function loadSplitTransactionFormModel(
  id: string
): Promise<Extract<TransactionFormModel, { mode: "split" }> | undefined> {
  const base = await getTransactionById(id);
  if (!base) return undefined;
  if (!base.split_group_id) {
    throw new Error("Transaction is not part of a split group.");
  }
  const user = await getUserId();
  const siblings = await db
    .collection("Transactions")
    .where("user", "==", user)
    .where("split_group_id", "==", base.split_group_id)
    .get();

  const rows: SplitRow[] = siblings.docs
    .map((d) => d.data() as any)
    .map((d) => ({
      id: d.id,
      amount: typeof d.amount === "number" ? d.amount : 0,
      gl_account_id: d.gl_account_id,
      account: d.account,
      description: d.description,
    }))
    .sort((a, b) => (a.id === base.id ? -1 : b.id === base.id ? 1 : 0));

  const total = rows.reduce((acc, r) => acc + (r.amount || 0), 0);

  return {
    transaction: base,
    mode: "split",
    split: { split_group_id: base.split_group_id, total, rows },
  };
}

/**
 * Delete every `Transactions` doc that shares the given `split_group_id`.
 *
 * Delegates to the `deleteSplitGroup` Cloud Function so the deletes (and the
 * shared receipt cleanup) reliably complete server-side even if the user
 * navigates away. Read-only rows are preserved by the function.
 */
export async function deleteSplitGroup(split_group_id: string): Promise<void> {
  if (!split_group_id) return;
  const fn = firebase.functions().httpsCallable("deleteSplitGroup");
  await fn({ split_group_id });
}

const UnsplitOmittedFields = [
  "id",
  "split_group_id",
  "vector",
  "accountDoc",
  "vendorDoc",
  "vendorName",
  "read_only",
] as const;

/**
 * Collapse a split group into one transaction. The Cloud Function writes the
 * new row and deletes the old rows in one Firestore transaction, then removes
 * a receipt file the new row does not still use. A replacement file is
 * uploaded here after that call returns the new Storage path.
 */
export async function unsplitTransaction(
  split_group_id: string,
  transaction: Partial<Transaction>,
  newImageFile?: File
): Promise<string> {
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(transaction)) {
    if (value !== undefined) fields[key] = value;
  }
  for (const key of UnsplitOmittedFields) delete fields[key];
  if (newImageFile) delete fields.receipt_filename;

  const fn = firebase.functions().httpsCallable("unsplitTransaction");
  const response = await fn({
    split_group_id,
    transaction: fields,
    ...(newImageFile ? { new_receipt_name: newImageFile.name } : {}),
  });
  const data = response.data as {
    id?: string;
    receipt_filename?: string;
  };
  if (!data?.id) {
    throw new Error("Unsplit did not return a transaction id");
  }
  if (newImageFile) {
    if (!data.receipt_filename) {
      throw new Error("Unsplit did not return a receipt path");
    }
    await uploadFile(data.receipt_filename, newImageFile, {
      contentType: receiptContentType(newImageFile),
    });
  }
  return data.id;
}
